import { DependencyList, EffectCallback, useCallback, useEffect, useRef, useState } from 'react';

import { useEvent } from './use-event';

type InitializationEffectCallback = (
  /** A function that **must** be called to signal that the initialization effect has been executed.  Calling this function is required to ensure that the initialization logic that the caller intends
   * to only execute once doesn't execute multiple times.
   *
   * **Important:** This function **CANNOT** be called from an async function or promise.  It must be called synchronously.  Otherwise the initialization effect will be executed multiple times.
   */
  setIsInitialized: () => void,
) => ReturnType<EffectCallback>;

/** Executes the initialization effect callback exactly 1 time.  This prevents double execution during development or when any dependencies change.  This supports waiting for any dependency conditions
 * to be met before the initialization effect is executed.  This is done by calling the provided `setIsInitialized` function to signal that the initializer has executed.
 *
 * **Important:** While this hook does support async initializers it is critical to only call the provided `setIsInitialized` **BEFORE** any async operations.  Otherwise the initialization effect will be
 * executed multiple times.
 *
 * @param callbackFn - The callback function to execute.
 * @param deps - The dependencies to trigger the internal useEffect to execute the callback function.
 */
export function useInitializationEffect(callbackFn: InitializationEffectCallback, deps: DependencyList) {
  const isInitializedRef = useRef(false);
  const [isInitialized, setIsInitializedInternal] = useState(false);

  const setIsInitialized = useCallback(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      setIsInitializedInternal(true);
    } else {
      // The initialization effect should only be executed once.
      throw new Error('The initialization effect has already been signaled as having been executed by a previous call to the setIsInitialized function.');
    }
  }, []);

  const stableCallbackFn = useEvent(callbackFn);

  useEffect(() => {
    if (!isInitializedRef.current) {
      return stableCallbackFn(setIsInitialized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Ignore exhaustive deps because the spread operator makes it hard for code analysis to enforce correctly.
  }, [stableCallbackFn, ...deps]);

  return isInitialized;
}
